import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/Survey/SetFieldValue";
import { SurveyFormContext } from "@hardware/Widgets/Survey/SurveyForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => ({
    lastName: state.surveyForm.fieldValues["lastName"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LastNameStyles {
    lastNameTextField?: TextFieldPresentationProps;
}

export const lastNameStyles: LastNameStyles = {};

const styles = lastNameStyles;

const LastName: React.FC<Props> = ({ fields, lastName, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const surveyFormContext = React.useContext(SurveyFormContext);
    const [lastNameErrorMessage, setLastNameErrorMessage] = React.useState("");

    React.useEffect(() => {
        surveyFormContext.validators.lastName = validateLastName;
        if (typeof lastName !== "undefined") {
            validateLastName();
        }
    }, [lastName]);

    React.useEffect(() => {
        surveyFormContext.validators.lastName = validateLastName;
    }, [isRequired]);

    const validateLastName = () => {
        const errorMessage = isRequired && !lastName ? (siteMessage("Field_Required", label) as string) : "";
        setLastNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "lastName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.lastNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={lastName || ""}
            error={lastNameErrorMessage}
            onChange={lastNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LastName),
    definition: {
        group: "Survey" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Last Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Last Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
